import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Loading from '../../../../components/Loading';
import Modal from '../../../../components/Modal/Modal';
import usePermissions from '../../../../components/Menu/MenuItems/hooks/usePermissions';
import { Container } from '../../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../../styles/layout/Section';
import { FormGrid } from '../../../../styles/layout/FormGrid';
import { Field } from '../../../../styles/forms/Field';
import { TableWrapper } from '../../../../styles/table/TableWrapper';
import { Table } from '../../../../styles/table/Table';
import { EmptyState } from '../../../../styles/feedback/EmptyState';
import {
   CtaButton,
   GhostButton,
   DangerButton,
   SmallButton,
} from '../../../../styles/ui/Button';
import {
   listRequirements,
   deleteRequirement,
} from '../../../../services/hr/jobPositionRequirementsApi';

import JobPositionSelect from '../components/JobPositionSelect';
import { REQUIREMENT_TYPE_LABEL } from '../constants';
import { recruitmentErrorMessage } from '../utils/apiError';
import RequirementFormModal from './components/RequirementFormModal';

// ---------------------------------------------------------------------------
// RH · Recrutamento — Requisitos por cargo (handoff 0065 §8). Catálogo de
// gestão no padrão OriginsCatalog (sem paginação/pesquisa): seleciona um cargo
// e gere os requisitos dele. Requisito DOCUMENT dirige o checklist de admissão
// (ex.: CNH quando o cargo efetivo exige). `?job_position_id=` vai na URL com
// replace:true — deep-link barato, sem poluir o histórico.
// ---------------------------------------------------------------------------

export default function PositionRequirementsCatalog() {
   const { hasPermission } = usePermissions();
   const canManage = hasPermission('hr:job_position_requirement:manage');

   const [searchParams, setSearchParams] = useSearchParams();
   const [jobPositionId, setJobPositionId] = useState(
      () => searchParams.get('job_position_id') ?? '',
   );

   const [requirements, setRequirements] = useState([]);
   const [isLoading, setIsLoading] = useState(false);

   const [formOpen, setFormOpen] = useState(false);
   const [deleteTarget, setDeleteTarget] = useState(null);
   const [isDeleting, setIsDeleting] = useState(false);

   const fetchRequirements = useCallback(async () => {
      if (!jobPositionId) {
         setRequirements([]);
         return;
      }
      setIsLoading(true);
      try {
         const list = await listRequirements(jobPositionId);
         setRequirements(list);
      } catch (err) {
         toast.error(
            recruitmentErrorMessage(err, 'Erro ao carregar os requisitos.'),
         );
      } finally {
         setIsLoading(false);
      }
   }, [jobPositionId]);

   useEffect(() => {
      fetchRequirements();
   }, [fetchRequirements]);

   const handleSelectPosition = (value) => {
      setJobPositionId(value);
      setSearchParams(value ? { job_position_id: value } : {}, {
         replace: true,
      });
   };

   const handleConfirmDelete = async () => {
      if (!deleteTarget || isDeleting) return;
      setIsDeleting(true);
      try {
         await deleteRequirement(jobPositionId, deleteTarget.id);
         toast.success('Requisito excluído.');
         setDeleteTarget(null);
         fetchRequirements();
      } catch (err) {
         toast.error(
            recruitmentErrorMessage(err, 'Erro ao excluir o requisito.'),
         );
         setDeleteTarget(null);
      } finally {
         setIsDeleting(false);
      }
   };

   const hasResults = requirements.length > 0;

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Requisitos por cargo</PageTitle>
               <PageSubtitle>
                  Requisitos de documento dirigem o checklist de admissão do
                  onboarding
               </PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               {canManage && (
                  <CtaButton
                     type="button"
                     onClick={() => setFormOpen(true)}
                     disabled={!jobPositionId}
                  >
                     + Novo requisito
                  </CtaButton>
               )}
            </PageHeaderActions>
         </PageHeader>

         <Section>
            <SectionHeader>
               <SectionTitle>Cargo</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <FormGrid>
                  <Field className="col-6">
                     <label htmlFor="requirements-job-position">
                        Selecione o cargo
                     </label>
                     <JobPositionSelect
                        id="requirements-job-position"
                        value={jobPositionId}
                        onChange={handleSelectPosition}
                     />
                  </Field>
               </FormGrid>
            </SectionBody>
         </Section>

         <Section>
            <SectionHeader>
               <SectionTitle>
                  Requisitos{hasResults ? ` (${requirements.length})` : ''}
               </SectionTitle>
            </SectionHeader>
            <SectionBody>
               {!jobPositionId ? (
                  <EmptyState
                     title="Selecione um cargo"
                     description="Escolha um cargo acima para ver e gerir os requisitos dele."
                  />
               ) : hasResults ? (
                  <TableWrapper>
                     <Table>
                        <thead>
                           <tr>
                              <th>Tipo</th>
                              <th>Documento</th>
                              <th>Rótulo</th>
                              <th className="center">Obrigatório</th>
                              <th>Observações</th>
                              {canManage && <th className="center">Ações</th>}
                           </tr>
                        </thead>
                        <tbody>
                           {requirements.map((requirement) => (
                              <tr key={requirement.id}>
                                 <td>
                                    {REQUIREMENT_TYPE_LABEL[
                                       requirement.requirement_type
                                    ] ?? requirement.requirement_type}
                                 </td>
                                 <td>
                                    {requirement.document_type?.name ?? '—'}
                                 </td>
                                 <td>{requirement.label ?? '—'}</td>
                                 <td className="center">
                                    {requirement.is_mandatory ? 'Sim' : '—'}
                                 </td>
                                 <td>{requirement.notes ?? '—'}</td>
                                 {canManage && (
                                    <td className="center">
                                       <SmallButton
                                          type="button"
                                          $intent="danger"
                                          onClick={() =>
                                             setDeleteTarget(requirement)
                                          }
                                       >
                                          Excluir
                                       </SmallButton>
                                    </td>
                                 )}
                              </tr>
                           ))}
                        </tbody>
                     </Table>
                  </TableWrapper>
               ) : (
                  <EmptyState
                     title="Nenhum requisito para este cargo"
                     description="Hoje, apenas requisitos do tipo Documento afetam o checklist de admissão (ex.: CNH)."
                  />
               )}
            </SectionBody>
         </Section>

         <RequirementFormModal
            isOpen={formOpen}
            onClose={() => setFormOpen(false)}
            jobPositionId={jobPositionId}
            onSaved={fetchRequirements}
         />

         <Modal
            isOpen={Boolean(deleteTarget)}
            onClose={() => setDeleteTarget(null)}
            title="Excluir requisito"
            maxWidth="440px"
            footer={
               <>
                  <GhostButton
                     type="button"
                     onClick={() => setDeleteTarget(null)}
                     disabled={isDeleting}
                  >
                     Cancelar
                  </GhostButton>
                  <DangerButton
                     type="button"
                     onClick={handleConfirmDelete}
                     disabled={isDeleting}
                  >
                     {isDeleting ? 'Excluindo…' : 'Excluir requisito'}
                  </DangerButton>
               </>
            }
         >
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
               Excluir este requisito? Novos checklists deixarão de exigir o
               documento; checklists já gerados não são alterados.
            </p>
         </Modal>
      </Container>
   );
}
